import React, { FC, useMemo } from 'react'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Button } from '@ucheba/ui/components/Button/desktop'
import { List, ListItem } from '@ucheba/ui/components/List/desktop'
import { Box } from '@ucheba/ui/components/Box/touch'
import IconCheck from '@ucheba/ui/icons/icon__check_circle-outline.svg'
import IconClose from '@ucheba/ui/icons/icon__close.svg'
import styled, { StyledProps } from 'styled-components'
import { sv } from '@ucheba/utils/helpers/styles/variables'
import { spaceDigits } from '@ucheba/utils/helpers/number'
import HelpIcon from '@ucheba/ui/icons/icon__help.svg'
import { Tooltip } from '@ucheba/ui/components/Tooltip/desktop/Tooltip'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { ITariffCardProps } from '../types'
import ImgHitChip from '../assets/icon__chips-size-small-color.png'

const DiscountChip = styled.div<StyledProps<any>>`
  padding: 0 4px;
  background-color: ${(props) => sv.color[props.color]};
  color: white;
  border-radius: 4px;
  height: 16px;
  display: flex;
  align-items: center;
`

const HitImg = styled.img`
  height: 28px;
`

const HelpIconST = styled(HelpIcon)`
  cursor: help;
  position: absolute;
  top: 4px;
  left: 4px;
`

const TooltipSC = styled(Tooltip)`
  display: inline-block;
  color: black;
`

const Chip = styled.div<StyledProps<any>>`
  padding: 2px 12px;
  border-radius: 16px;
  border: 1px solid ${(props) => (props.color ? sv.color[props.color] : sv.color.gold)};
  background-color: ${(props) => (!props.active ? sv.color.transparent : sv.color.gold)};
`

const TariffCard: FC<ITariffCardProps> = (props) => {
  const {
    name,
    list,
    text,
    price,
    discount,
    onClick,
    hit,
    count,
    helping,
    tooltipWidth,
    date,
    onPay,
  } = props

  const priceMonth = useMemo(() => {
    if (discount) {
      const p = (discount / 100) * price.forMonth

      return `${spaceDigits(Math.floor((price.forMonth - p) * count))} р.`
    }

    return `${spaceDigits(price.forMonth * count)} р.`
  }, [count, discount, price])

  const priceYear = useMemo(() => {
    if (discount) {
      const p = (discount / 100) * price.forYear

      return `${spaceDigits(Math.floor(price.forYear - p) * count)} р.`
    }

    return `${spaceDigits(price.forYear)} р.`
  }, [discount, price])

  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      <Box spacing='medium' color={hit ? 'plum' : 'white'}>
        <Grid spacing='usmall' justifyContent='center'>
          {date && (
            <GridCol width='auto'>
              <Chip color={hit ? 'gold' : 'plum'}>
                <Text color={hit ? 'gold' : 'plum'} fontSize='small2'>
                  {date}
                </Text>
              </Chip>
            </GridCol>
          )}

          {hit && (
            <GridCol width='auto'>
              <HitImg src={ImgHitChip} />
            </GridCol>
          )}
        </Grid>

        <Spacing spacing='usmall' />

        <Heading
          h2={name}
          spacing='usmall'
          color={hit ? 'white' : 'plum'}
          textAlign='center'
        />

        <Text fontWeight='semibold' textAlign='center'>
          {text}
        </Text>

        <Spacing spacing='xsmall' />

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div>
            <Text
              fontSize='h3'
              lineHeight='h3'
              color={hit ? 'gold' : 'orange'}
              textAlign='center'
              fontWeight='bold'
            >
              {priceMonth}
            </Text>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!!discount && (
                <Text
                  fontWeight='bold'
                  color={hit ? 'white50' : 'gray40'}
                  textDecoration='lineThrough'
                >
                  {`${spaceDigits(price.forMonth * count)} p.`}
                </Text>
              )}

              {!!discount && (
                <div
                  style={{
                    padding: '4px',
                  }}
                >
                  <DiscountChip color={hit ? 'gold' : 'orange'}>
                    <Text
                      fontSize='small2'
                      fontWeight='semibold'
                      color={hit ? 'black' : 'white'}
                    >
                      {`-${discount}%`}
                    </Text>
                  </DiscountChip>
                </div>
              )}
            </div>

            <Text textAlign='center'>за месяц</Text>
          </div>

          <div
            style={{
              padding: '8px 10px',
              minHeight: '100%',
              width: '1px',
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(3, 15, 35, 0.09)',
                width: '1px',
                height: '100%',
              }}
            />
          </div>

          <div>
            <Text fontSize='h3' lineHeight='h3' textAlign='center'>
              {priceYear}
            </Text>

            {!!discount && (
              <div>
                <Text color='gray40' textDecoration='lineThrough' textAlign='center'>
                  {`${spaceDigits(price.forYear * count)} p.`}
                </Text>
              </div>
            )}

            <Text textAlign='center'>за весь курс</Text>
          </div>
        </div>

        <Spacing spacing='medium' />

        <Button
          block
          color={hit ? 'gold' : 'plum'}
          onClick={() => {
            if (onClick) {
              onClick()
            }
          }}
        >
          Отправить заявку
        </Button>

        <Spacing spacing='small' />

        <Button
          block
          view='outline'
          color={hit ? 'gold' : 'plum'}
          onClick={() => {
            if (onPay) {
              onPay()
            }
          }}
        >
          Купить сейчас
        </Button>

        <Spacing spacing='small' />

        <List spacing='xsmall'>
          {list.map((listItem, key) => {
            if (listItem.disable) {
              return (
                <ListItem
                  startIconSpacing='xxsmall'
                  key={key}
                  StartIcon={
                    <IconClose color={hit ? 'white50' : 'gray40'} width='16px' />
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <Text
                      color={hit ? 'white50' : 'gray40'}
                      textDecoration='lineThrough'
                      html={false}
                    >
                      {listItem.text}

                      {helping && listItem.help && (
                        <TooltipSC
                          width={tooltipWidth || '320px'}
                          position='left-top'
                          content={listItem.help}
                        >
                          <div style={{ position: 'relative', height: '20px' }}>
                            <HelpIconST
                              width='16px'
                              height='16px'
                              color={hit ? 'white50' : 'gray40'}
                            />
                          </div>
                        </TooltipSC>
                      )}
                    </Text>
                  </div>
                </ListItem>
              )
            }

            return (
              <ListItem
                startIconSpacing='xxsmall'
                key={key}
                StartIcon={<IconCheck color={hit ? 'gold' : 'plum'} width='16px' />}
              >
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <Text color={hit ? 'white' : 'black'} html={false}>
                    {listItem.text}

                    {helping && listItem.help && (
                      <TooltipSC
                        width={tooltipWidth || '320px'}
                        position='left-top'
                        content={listItem.help}
                      >
                        <div style={{ position: 'relative', height: '20px' }}>
                          <HelpIconST
                            width='16px'
                            height='16px'
                            color={hit ? 'white50' : 'gray40'}
                          />
                        </div>
                      </TooltipSC>
                    )}
                  </Text>
                </div>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </div>
  )
}

export default TariffCard
